import type { ReactNode } from "react";
import { Fieldset } from "@base-ui/react/fieldset";
import classNames from "classnames";
import { useFormikFieldState } from "@components/forms/FormikField/FormikField";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { ChoiceFieldShell } from "./ChoiceFieldShell";
import type { FieldPath, FieldPathValue } from "./fieldPath.types";
import type { KitChoiceFieldProps } from "./kitFieldProps.types";
import styles from "./ChoiceFields.module.scss";

type RadioFieldValue = string | null | undefined;

/** Paths of `Values` that hold a string choice. */
export type RadioFieldPath<Values> = FieldPath<Values, RadioFieldValue>;

/** The option values a path accepts: its string members. */
export type RadioFieldOptionValue<Values, Name extends string> = Extract<
    FieldPathValue<Values, Name>,
    string
>;

export interface RadioFieldOption<Option> {
    value: Option;
    label: ReactNode;
    disabled?: boolean;
}

export type RadioFieldProps<Values, Name extends string> = KitChoiceFieldProps<
    Name,
    HTMLDivElement,
    RadioFieldOptionValue<Values, Name>
> & {
    /** Every option value must fit the path, so a pick stays in type. */
    options: ReadonlyArray<
        RadioFieldOption<RadioFieldOptionValue<Values, Name>>
    >;
};

/**
 * Radio group bound to a typed Formik path. The group is a `fieldset` whose
 * legend reads like a `Field` label and names the radiogroup for screen
 * readers (`labelHidden` hides the legend visually). With `aria-labelledby`
 * the radiogroup is named by that id and renders without a fieldset, which
 * would otherwise be an unnamed group. The error shows below after the group
 * loses focus or on submit. `ref` points at the radiogroup element.
 */
export const RadioField = <
    Values,
    Name extends RadioFieldPath<Values> = RadioFieldPath<Values>,
>({
    name,
    label,
    labelHidden,
    "aria-labelledby": ariaLabelledby,
    optional,
    description,
    className,
    showErrorMessage,
    options,
    ref,
    disabled,
    readOnly,
    required,
    onValueChange,
}: RadioFieldProps<Values, Name>) => {
    const { field, helpers, invalid, error, rootRef } =
        useFormikFieldState<RadioFieldValue>({ name, showErrorMessage });
    const selectedOption = options.find(
        (option) => option.value === field.value,
    );

    return (
        <ChoiceFieldShell
            ref={rootRef}
            invalid={invalid}
            error={error}
            description={description}
            className={className}
            render={
                ariaLabelledby === undefined ? (
                    <Fieldset.Root className={styles.fieldset} />
                ) : undefined
            }
        >
            {ariaLabelledby === undefined && (
                <Fieldset.Legend
                    className={classNames(
                        styles.legend,
                        labelHidden && styles.legendHidden,
                    )}
                >
                    <span>{label}</span>
                    {optional && (
                        <span className={styles.optional}>(optional)</span>
                    )}
                </Fieldset.Legend>
            )}
            <RadioGroup<RadioFieldOptionValue<Values, Name>>
                ref={ref}
                name={name}
                aria-labelledby={ariaLabelledby}
                value={selectedOption?.value ?? null}
                onValueChange={(value) => {
                    void helpers.setValue(value);
                    onValueChange?.(value);
                }}
                onBlur={() => void helpers.setTouched(true)}
                invalid={invalid}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
            >
                {options.map((option) => (
                    <Radio
                        key={option.value}
                        value={option.value}
                        label={option.label}
                        disabled={option.disabled}
                    />
                ))}
            </RadioGroup>
        </ChoiceFieldShell>
    );
};
